import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { brochuresAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Brochures.css';

const emptyForm = { title: '', description: '', details: '', category: 'other', sortOrder: 0 };

const BrochuresAdmin = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [brochures, setBrochures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [cardImageFile, setCardImageFile] = useState(null);
  const [editingId, setEditingId] = useState(null); // null = create mode
  const [draggedId, setDraggedId] = useState('');
  const [orderDirty, setOrderDirty] = useState(false);
  const formRef = React.useRef(null);

  const canManage = user?.role === 'admin' || user?.role === 'manager';

  const fetchBrochures = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await brochuresAPI.getAll();
      const sorted = [...(data || [])].sort((a, b) => {
        const aOrder = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
        const bOrder = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a?.title || '').localeCompare(String(b?.title || ''));
      });
      setBrochures(sorted);
      setOrderDirty(false);
    } catch (loadError) {
      setError(loadError.message || 'Brochures laden is mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrochures();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setFile(null);
    setCardImageFile(null);
    setEditingId(null);
    formRef.current?.reset();
  };

  const handleEdit = (brochure) => {
    setEditingId(brochure.id);
    setForm({
      title: brochure.title || '',
      description: brochure.description || '',
      details: brochure.details || '',
      category: brochure.category || 'other',
      sortOrder: Number.isFinite(Number(brochure.sortOrder)) ? Number(brochure.sortOrder) : 0
    });
    setFile(null);
    setCardImageFile(null);
    setError('');
    setSuccess('');
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!canManage) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        await brochuresAPI.update(editingId, { ...form, cardImageFile }, file);
        setSuccess('Brochure succesvol bijgewerkt.');
      } else {
        await brochuresAPI.create({ ...form, cardImageFile }, file);
        setSuccess('Brochure succesvol toegevoegd.');
      }
      resetForm();
      await fetchBrochures();
    } catch (saveError) {
      setError(saveError.message || 'Opslaan is mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!canManage) return;
    if (!window.confirm('Weet je zeker dat je deze brochure wilt verwijderen?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await brochuresAPI.delete(id);
      setSuccess('Brochure succesvol verwijderd.');
      await fetchBrochures();
    } catch (deleteError) {
      setError(deleteError.message || 'Verwijderen is mislukt');
    }
  };

  const moveItem = (items, fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return items;
    const fromIndex = items.findIndex((item) => item.id === fromId);
    const toIndex = items.findIndex((item) => item.id === toId);
    if (fromIndex < 0 || toIndex < 0) return items;

    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  };

  const handleRowDrop = (targetId) => {
    if (!canManage) return;
    setBrochures((prev) => {
      const next = moveItem(prev, draggedId, targetId);
      const changed = next.some((item, index) => item.id !== prev[index]?.id);
      if (changed) setOrderDirty(true);
      return next;
    });
    setDraggedId('');
  };

  const saveOrder = async () => {
    if (!canManage || !orderDirty) return;

    setSavingOrder(true);
    setError('');
    setSuccess('');

    try {
      await Promise.all(
        brochures.map((item, index) => brochuresAPI.update(item.id, { sortOrder: index + 1 }, null))
      );
      setSuccess('Volgorde succesvol opgeslagen.');
      await fetchBrochures();
    } catch (saveOrderError) {
      setError(saveOrderError.message || 'Volgorde opslaan is mislukt');
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className="admin-page brochures-admin-page">
      <div className="page-header">
        <h1>{t('admin.brochures')}</h1>
        <p>Beheer brochures en controleer of bestanden correct laden vanuit de backend.</p>
      </div>

      {error && <div className="feedback error">{error}</div>}
      {success && <div className="feedback success">{success}</div>}

      {canManage && (
        <form ref={formRef} className="brochure-create-form" onSubmit={handleSave}>
          <div className="brochure-form-mode-header">
            <h3>{editingId ? 'Brochure bewerken' : 'Nieuwe brochure toevoegen'}</h3>
            {editingId && (
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Annuleren
              </button>
            )}
          </div>
          <div className="brochure-form-grid">
            <label>
              Titel *
              <input
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                required
              />
            </label>
            <label>
              Categorie
              <select
                value={form.category}
                onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
              >
                <option value="hbo">HBO</option>
                <option value="master">Master</option>
                <option value="training">Training</option>
                <option value="other">Overig</option>
              </select>
            </label>
            <label>
              Volgorde
              <input
                type="number"
                value={form.sortOrder}
                onChange={(event) => setForm((prev) => ({ ...prev, sortOrder: Number(event.target.value) }))}
              />
            </label>
            <label className="full-width">
              Beschrijving
              <textarea
                rows={3}
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
              />
            </label>
            <label className="full-width">
              Detailtekst (zichtbaar na klik op card)
              <textarea
                rows={8}
                value={form.details}
                onChange={(event) => setForm((prev) => ({ ...prev, details: event.target.value }))}
              />
            </label>
            <label className="full-width">
              Bestand (PDF/JPG/PNG/DOC)
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </label>
            <label className="full-width">
              Card afbeelding (JPG/PNG/WEBP)
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={(event) => setCardImageFile(event.target.files?.[0] || null)}
              />
            </label>
          </div>

          {editingId && (
            <p className="brochure-file-note">
              Alleen een nieuw bestand uploaden als je het huidige wilt vervangen. Leeg laten om het bestaande te behouden.
            </p>
          )}

          <button className="btn-primary" type="submit" disabled={saving}>
            {saving
              ? 'Opslaan...'
              : editingId
              ? 'Wijzigingen opslaan'
              : 'Brochure opslaan'}
          </button>
        </form>
      )}

      <div className="brochure-list-card">
        <div className="brochure-list-head">
          <h3>Beschikbare brochures</h3>
          <div className="brochure-list-head-actions">
            {canManage && (
              <button className="btn-secondary" onClick={saveOrder} disabled={!orderDirty || savingOrder}>
                {savingOrder ? 'Volgorde opslaan...' : 'Volgorde opslaan'}
              </button>
            )}
            <button className="btn-secondary" onClick={fetchBrochures} disabled={loading || savingOrder}>
              {loading ? 'Vernieuwen...' : 'Vernieuwen'}
            </button>
          </div>
        </div>

        {canManage && (
          <p className="brochure-file-note">Sleep rijen om de volgorde te wijzigen en klik daarna op "Volgorde opslaan".</p>
        )}

        {loading ? (
          <p>Brochures laden...</p>
        ) : brochures.length === 0 ? (
          <p>Geen brochures gevonden.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {canManage && <th>Sorteren</th>}
                  <th>Titel</th>
                  <th>Categorie</th>
                  <th>Volgorde</th>
                  <th>Bestand</th>
                  <th>Card Foto</th>
                  <th>Load Check</th>
                  {canManage && <th>Acties</th>}
                </tr>
              </thead>
              <tbody>
                {brochures.map((brochure) => (
                  <tr
                    key={brochure.id}
                    draggable={canManage}
                    onDragStart={() => setDraggedId(brochure.id)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleRowDrop(brochure.id)}
                    onDragEnd={() => setDraggedId('')}
                    className={draggedId === brochure.id ? 'brochure-row-dragging' : ''}
                  >
                    {canManage && <td className="brochure-drag-cell" title="Sleep om te sorteren">::</td>}
                    <td>{brochure.title}</td>
                    <td>{brochure.category || '-'}</td>
                    <td>{Number.isFinite(Number(brochure.sortOrder)) ? brochure.sortOrder : 0}</td>
                    <td>{brochure.file?.originalName || brochure.file?.filename || 'Geen bestand'}</td>
                    <td>{brochure.hasCardImage ? 'Ja' : 'Nee'}</td>
                    <td>
                      {brochure.id ? (
                        <>
                          <a href={brochuresAPI.viewUrl(brochure.id)} target="_blank" rel="noreferrer">Bekijken</a>
                          {' | '}
                          <a href={brochuresAPI.downloadUrl(brochure.id)} target="_blank" rel="noreferrer">Download</a>
                          {brochure.hasCardImage && (
                            <>
                              {' | '}
                              <a href={brochuresAPI.cardImageUrl(brochure.id)} target="_blank" rel="noreferrer">Card foto</a>
                            </>
                          )}
                        </>
                      ) : (
                        '-'
                      )}
                    </td>
                    {canManage && (
                      <td className="actions-cell">
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(brochure)}
                        >
                          Bewerken
                        </button>
                        <button
                          className="btn-danger"
                          onClick={() => handleDelete(brochure.id)}
                        >
                          Verwijderen
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrochuresAdmin;
